/* Piex controls the native Wokiee Swiper without changing other sliders. */
if (!customElements.get('piex-hero-controls')) {
  customElements.define('piex-hero-controls', class extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      this.host = this.closest('[data-piex-hero]');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.paused = this.dataset.autoplay !== 'true' || this.dataset.editor === 'true' || this.motion.matches;
      this.hovered = false;
      this.visible = true;
      this.delay = Math.max(2000, Number(this.dataset.delay) || 7000);
      this.initialize();
    }
    initialize() {
      if (!this.isConnected) return;
      if (!this.host.getBoundingClientRect().width) {
        if (!this.waitForVisible) {
          this.waitForVisible = new ResizeObserver(() => {
            if (!this.host.getBoundingClientRect().width) return;
            this.waitForVisible.disconnect();
            this.waitForVisible = null;
            this.initialize();
          });
          this.waitForVisible.observe(this.host);
        }
        return;
      }
      if (!this.host.swiper || !this.host.swiper.initialized) {
        this.frame = setTimeout(() => this.initialize(), 150);
        return;
      }
      this.slider = this.host.swiper;
      this.slider.update();
      this.slider.autoplay.stop();
      this.slider.params.speed = this.motion.matches ? 0 : 400;
      this.dots = this.querySelector('.piex-hero-dots');
      this.toggle = this.querySelector('[data-toggle]');
      this.dots.replaceChildren();
      [...this.slider.slides].forEach((slide, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.setAttribute('aria-label', 'Ir a diapositiva ' + (index + 1));
        button.dataset.index = index;
        this.dots.append(button);
        slide.setAttribute('aria-roledescription', 'diapositiva');
        slide.setAttribute('aria-label', (index + 1) + ' de ' + this.slider.slides.length);
      });
      const options = {signal: this.abort.signal};
      this.addEventListener('click', event => {
        const button = event.target.closest('button');
        if (!button) return;
        if (button.hasAttribute('data-toggle')) this.paused = !this.paused;
        else {
          this.paused = true;
          if (button.hasAttribute('data-prev')) this.slider.slidePrev();
          if (button.hasAttribute('data-next')) this.slider.slideNext();
          if (button.hasAttribute('data-index')) this.slider.slideTo(Number(button.dataset.index));
        }
        this.update();
      }, options);
      this.host.addEventListener('mouseenter', () => { this.hovered = true; this.schedule(); }, options);
      this.host.addEventListener('mouseleave', () => { this.hovered = false; this.schedule(); }, options);
      this.host.addEventListener('focusin', event => {
        // The toggle itself must preserve its advertised action when it receives focus.
        if (event.target.closest('[data-toggle]')) return;
        this.paused = true;
        this.update();
      }, options);
      document.addEventListener('visibilitychange', () => this.schedule(), options);
      this.motion.addEventListener('change', () => {
        this.slider.params.speed = this.motion.matches ? 0 : 400;
        if (this.motion.matches) this.paused = true;
        this.update();
      }, options);
      document.addEventListener('shopify:block:select', event => {
        const index = [...this.slider.slides].findIndex(slide => slide.contains(event.target));
        if (index >= 0) { this.paused = true; this.slider.slideTo(index, 0); this.update(); }
      }, options);
      this.changed = () => this.update();
      this.dragged = () => { this.paused = true; this.update(); };
      this.slider.on('slideChange', this.changed);
      this.slider.on('sliderFirstMove', this.dragged);
      this.observer = new IntersectionObserver(entries => {
        const wasVisible = this.visible;
        this.visible = entries[0].isIntersecting;
        // A mode can initialize while hidden; measure its slides when it becomes visible.
        if (this.visible && !wasVisible) this.slider.update();
        this.schedule();
      });
      this.observer.observe(this.host);
      this.update();
    }
    update() {
      [...this.slider.slides].forEach((slide, index) => {
        const active = index === this.slider.activeIndex;
        slide.inert = !active;
        slide.setAttribute('aria-hidden', String(!active));
        this.dots.children[index].setAttribute('aria-current', String(active));
      });
      this.host.dataset.activeSlide = this.slider.activeIndex;
      this.toggle.textContent = this.paused ? 'Reanudar' : 'Pausar';
      this.toggle.setAttribute('aria-label', this.paused ? 'Reanudar rotación' : 'Pausar rotación');
      this.host.dataset.rotation = this.paused ? 'paused' : 'playing';
      this.schedule();
    }
    schedule() {
      clearTimeout(this.timer);
      if (!this.slider || this.paused || this.hovered || document.hidden || !this.visible || this.dataset.editor === 'true') return;
      this.timer = setTimeout(() => this.slider.slideNext(), this.delay);
    }
    disconnectedCallback() {
      clearTimeout(this.timer);
      clearTimeout(this.frame);
      this.abort?.abort();
      this.observer?.disconnect();
      this.waitForVisible?.disconnect();
      this.slider?.off('slideChange', this.changed);
      this.slider?.off('sliderFirstMove', this.dragged);
    }
  });
}
